import {Engine} from "platform/engine/Engine";
import {DoLogout, SetAuthStatus, SetAuthStatusPayload} from "core/redux/auth/AuthReduxActions";
import Platform from "platform/Platform";
import {StoreState} from "core/redux/StoreState";
import Utils from "platform/util/Utils";
import {GetAccountStateRequest} from "protocol/account/GetAccountStateRequest";
import {HttpReject} from "platform/network/http/Http";
import {BadRequestResponse} from "protocol/BadRequestResponse";
import {
    CreditPromo,
    GetAccountStateResponse,
    MarginEvent,
    RebatePlanData,
    VaultInfo
} from "protocol/account/GetAccountStateResponse";
import {GetAccountStateAndRangesResponse} from "protocol/account/GetAccountStateAndRangesResponse";
import {
    DoAcceptCreditPromoPayload,
    OnProcessTopUpButtonPayload,
    SetAccountInfo,
    SetPromotionsChanged
} from "core/redux/account/AccountReduxActions";
import {XhrUtil} from "core/util/XhrUtil";
import AuthEngine from "core/engine/AuthEngine";
import {ServiceType} from "enum/ServiceType";
import {SetPositionsOrders} from "core/redux/trades/TradesReduxActions";
import {SetQuotes} from "core/redux/quotes/QuotesReduxActions";
import {AccountState} from "core/state/AccountState";
import {TSMap} from "typescript-map";
import {Quote} from "platform/protocol/trading/Quote";
import ChartEngine from "core/engine/ChartEngine";
import {ChartState} from "core/state/ChartState";
import {TradeUtil} from "core/util/TradeUtil";
import {GoingOpenDeal, SetDealRanges} from "core/redux/deal/DealReduxActions";
import {
    DoCancelWithdrawalPayload,
    DoWithdrawalPayload,
    SetPendingWithdrawal
} from "core/redux/account/WithdrawalReduxActions";
import {GetPendingWithdrawalRequest} from "protocol/withdrawal/GetPendingWithdrawalRequest";
import {ServerType} from "platform/enum/ServerType";
import {GetPendingWithdrawalResponse} from "protocol/withdrawal/GetPendingWithdrawalResponse";
import {CreateWithdrawalRequest} from "protocol/withdrawal/CreateWithdrawalRequest";
import {HideLoader, SetLoader} from "platform/redux/core/CoreActions";
import {LoaderType} from "platform/enum/LoaderType";
import {HidePopupPayload, ShowPopup} from "platform/redux/popups/PopupsActions";
import {PopupAction, PopupActionType, PopupIconType, PopupType} from "platform/redux/popups/PopupsReduxState";
import {LoginAccountInfo} from "protocol/auth/LoginAccountInfo";
import {AuthState} from "core/state/AuthState";
import {CancelWithdrawalRequest} from "protocol/withdrawal/CancelWithdrawalRequest";
import {WithdrawalPending} from "core/redux/account/WithdrawalReduxState";
import {GetCurrentChartQuotesRequest} from "platform/protocol/trading/chart/GetCurrentChartQuotesRequest";
import {RouteType} from "core/router/Routes";
import {BannerPayload, SetBanner, SetTradingModal} from "core/redux/app/AppReduxActions";
import TradingModalType from "enum/TradingModalType";
import {AddInboxMessage} from "core/redux/inbox/InboxReduxActions";
import InboxMessageType from "enum/InboxMessageType";
import Parameter from "platform/util/Parameter";
import InboxParameter from "enum/InboxParameter";
import {TranslationKey} from "enum/TranslationKey";
import {TranslationParam} from "enum/TranslationParam";
import {BannerType} from "enum/BannerType";
import {SymbolsState} from "core/state/SymbolsState";
import {QuotesState} from "core/state/QuotesState";
import {PopupActionType as PopupNotificationActionType, PopupNotification} from "protocol/account/PopupNotification";
import {PositionDirection} from "platform/protocol/enum/PositionDirection";
import {NXEnvironmentType} from "platform/protocol/enum/NXEnvironmentType";
import {AccountOption} from "protocol/account/AccountOption";
import {PromotionUtil} from "core/util/PromotionUtil";
import {PositionPromotion} from "platform/protocol/trading/PositionPromotion";
import {AccountReduxState} from "core/redux/account/AccountReduxState";
import {DepositButtonActionType} from "protocol/sso/DepositButtonActionType";
import {CreditPromoAcceptRequest} from "protocol/promo/CreditPromoAcceptRequest";
import {CreditPromoStatus} from "protocol/promo/CreditPromoStatus";
import {CreditPromoAcceptResponse} from "protocol/promo/CreditPromoAcceptResponse";
import {Currency} from "platform/enum/Currency";
import {NumberFormat} from "core/format/NumberFormat";
import AppEngine from "core/engine/AppEngine";
import {ExecuteDpk} from "platform/redux/dpk/DpkActions";
import {Configuration} from "core/configuration/ConfigManager";
import SessionInactivity from "core/util/SessionInactivity";
import {TradeSymbol} from "platform/protocol/trading/symbol/TradeSymbol";
import Translations from "platform/translation/Translations";

export default class AccountEngine extends Engine {

    private static _instance: AccountEngine;
    private _accountInterval: any;
    private _promotionsInterval: any;
    private _isFirstAccountState: boolean = true;
    private _lastPopupNotification: PopupNotification;
    private _lastPopupNotificationId: number;
    private _lastMarginEvent: MarginEvent;
    private _lastMarginEventId: number;
    private _lastCreditPromo: CreditPromo;
    private _lastCreditPromoId: number;

    public static instance(): AccountEngine {
        return this._instance || (this._instance = new this());
    }

    public doSetAuthStatus = (payload: SetAuthStatusPayload): void => {
        const {connected, authenticated} = Platform.reduxState<StoreState>().auth;
        if (connected) {
            if (!authenticated && payload.authStatus.authenticated && Utils.isNull(this._accountInterval)) {
                this.doGetAccountInfo();
                this.doFetchPendingWithdrawal().catch(() => {
                });
                this._logger.debug("Start get account state tracker.");
                this._accountInterval = setInterval(this.doGetAccountInfo, 500);
            }
        }
    }

    public onAccountChanged = (): void => {
        this._isFirstAccountState = true;
        this.doFetchPendingWithdrawal().catch(() => {
        });
    }

    public onLoggedOut = () => {
        this._isFirstAccountState = true;
        if (Utils.isNotNull(this._accountInterval)) {
            this._logger.debug("On logged out. Stop get account state tracker.");
            clearInterval(this._accountInterval);
            clearInterval(this._promotionsInterval);
            this._accountInterval = null;
            this._promotionsInterval = null;
        }
    }

    private doGetAccountInfo = (): void => {
        if (!Platform.reduxState<StoreState>().auth.connected) {
            return;
        }
        const symbolsState: SymbolsState = Platform.state(ServiceType.Symbols);
        const chartState: ChartState = Platform.state(ServiceType.Chart);
        const chartSubscriptions: GetCurrentChartQuotesRequest[] = chartState.getSubscriptions();
        const request: GetAccountStateRequest = {
            actionFormData: TradeUtil.mapActionFormData(),
            lastHandledChatNotification: null,
            lastHandledCreditPromo: this._lastCreditPromoId,
            lastHandledMarginEvent: this._lastMarginEventId,
            lastHandledPopupNotification: this._lastPopupNotificationId,
            lastSelectedSymbolId: null,
            GetCurrentChartQuotesRequests: Utils.isArrayNotEmpty(chartSubscriptions) ? chartSubscriptions : null,
            AdditionalSymbolsIds: [...symbolsState.additionalSymbolsIds, ...chartSubscriptions.map((cs: GetCurrentChartQuotesRequest) => cs.SymbolId)]
        };
        const path: string = Utils.isNotNull(request.actionFormData) ? "GetAccountStateAndRanges" : "GetAccountState";
        this.sendToAccountService(request, path)
            .then((response: any) => {
                if (Platform.reduxState<StoreState>().auth.authenticated) {
                    if (Utils.isNotNull(response.Item2)) {
                        this.doProcessAccountStateAndRange(response);
                    } else {
                        this.doProcessAccountState(response);
                    }
                }
            })
            .catch((error: HttpReject) => {
                if (error) {
                    if (error.status === 0) {
                        this._logger.debug("Disconnected");
                        Platform.dispatch(SetAuthStatus({
                            authStatus: {connected: false}
                        }));
                        const authEngine: AuthEngine = Platform.engine(ServiceType.Auth);
                        authEngine.startPing();
                    } else if (Utils.isNotNull(error.response)) {
                        let response: BadRequestResponse;
                        try {
                            response = JSON.parse(error.response);
                        } catch (e) {
                            this._logger.warn("Can't parse get account state failed response");
                        }
                        if (response && response.IsSecurityException) {
                            this._logger.warn("Security exception fetch account info. Stop interval");
                            if (Utils.isNotNull(this._accountInterval)) {
                                clearInterval(this._accountInterval);
                                clearInterval(this._promotionsInterval);
                                this._accountInterval = null;
                                this._promotionsInterval = null;
                            }
                            if (!Platform.reduxState<StoreState>().auth.loggedOff) {
                                Platform.dispatch(DoLogout({skipNotifyServer: true}));
                            }
                        }
                    }
                }
            });
    }

    private doProcessAccountState = (response: GetAccountStateResponse) => {
        const accountState: AccountState = Platform.state(ServiceType.Account);
        if (response.AccountId === accountState.accountId && Utils.isNotNull(response.AggregateData)) {
            const chartEngine: ChartEngine = Platform.engine(ServiceType.Chart);
            accountState.accountId = response.AccountId;
            accountState.depositButtonType = response.DepositButtonActionType;
            const {AccountRebateData, Vault, PositionPromotionSettings} = Platform.reduxState<StoreState>().account;
            Platform.dispatch(SetAccountInfo({
                accountInfo: {
                    ...response.AggregateData,
                    AccountId: response.AccountId,
                    MarginCallPercentage: response.MarginCallPercentage,
                    AccountRebateData: response.AccountRebateData,
                    Vault: response.Vault,
                    PositionPromotionSettings: response.PositionPromotionSettings,
                    IsUserAllowedToTrade: response.IsUserAllowedToTrade,
                    ShouldShowInappropriatePopup: response.ShouldShowInappropriatePopup,
                    ShownMenuItems: response.ShownMenuItems,
                }
            }));
            const quotes: TSMap<number, Quote> = Platform.reduxState<StoreState>().quotes.quotes.clone();
            if (Utils.isArrayNotEmpty(response.Quotes)) {
                response.Quotes.forEach((quote: Quote) => {
                    quotes.set(quote.SymbolId, quote);
                    chartEngine.doUpdateQuoteState(quote);
                });
            }
            const quotesState: QuotesState = Platform.state(ServiceType.Quotes);
            quotesState.quotes = quotes;
            Platform.dispatch(SetQuotes({quotes}));
            Platform.dispatch(SetPositionsOrders({
                OpenPositions: response.OpenPositions,
                EntryOrders: response.EntryOrders,
                Exposures: response.Exposures
            }));
            chartEngine.doUpdateChart(response.Quotes, response.CurrentChartQuotes, response.OpenPositions);
            this.doProcessRebateData(AccountRebateData, response);
            this.doProcessMarginEvents(response);
            this.doProcessCreditPromos(response);
            this.doProcessPopupNotifications(response);
            this.doProcessAccountOptions(response);
            this.doProcessPromotions(AccountRebateData, Vault, PositionPromotionSettings, response);
            if (this._isFirstAccountState) {
                const hasMoney: boolean = Utils.greaterThen0(response.AggregateData?.Balance);
                const {inActiveTraderIdleTimeBeforeLogoff, activeTraderIdleTimeBeforeLogoff} = Platform.config<Configuration>();
                SessionInactivity.setThreshold(hasMoney ? activeTraderIdleTimeBeforeLogoff : inActiveTraderIdleTimeBeforeLogoff);
                SessionInactivity.reset();
                const appEngine: AppEngine = Platform.engine(ServiceType.App);
                appEngine.tryExecuteDpkUrl();
            }
            this._isFirstAccountState = false;
        }
    }

    private doProcessAccountStateAndRange = (response: GetAccountStateAndRangesResponse) => {
        this.doProcessAccountState(response.Item1);
        if (response.Item2) {
            Platform.dispatch(SetDealRanges({
                ranges: response.Item2
            }));
        }
    }

    private doProcessRebateData = (AccountRebateData: RebatePlanData, response: GetAccountStateResponse) => {
        if (response.AccountRebateData) {
            const accountReduxState: AccountReduxState = Platform.reduxState<StoreState>().account;
            if (AccountRebateData?.CurrentRebateLevelId && response.AccountRebateData.CurrentRebateLevelId !== AccountRebateData?.CurrentRebateLevelId) {
                Platform.dispatch(ShowPopup({
                    popup: {
                        type: PopupType.INFO,
                        icon: PopupIconType.INFO,
                        showClose: true,
                        message: {
                            trKey: response.AccountRebateData.IsFinalLevel ? TranslationKey.promotionModalRebatePlanInfoReachedLastLevel : TranslationKey.promotionModalRebatePlanInfoReachedNextLevel,
                            params: [
                                Parameter.Of(TranslationParam.amount, NumberFormat.formatMoney(response.AccountRebateData.CurrentRebate, accountReduxState.Currency)),
                                Parameter.Of(TranslationParam.value, NumberFormat.formatMoney(response.AccountRebateData.VolumeGapFromNextLevelInRequestedAsset, accountReduxState.Currency))
                            ]
                        },
                        actions: [{type: PopupActionType.OK}]
                    }
                }));
            }
        }
    }

    private doProcessMarginEvents = (response: GetAccountStateResponse) => {
        this._lastMarginEventId = null;
        if (Utils.isArrayNotEmpty(response.MarginEvents) && Utils.isNull(this._lastMarginEvent)) {
            let lastEvent: MarginEvent = response.MarginEvents[0];
            for (let i = 1; i < response.MarginEvents.length; i++) {
                if (lastEvent.EventType === response.MarginEvents[i].EventType) {
                    lastEvent = response.MarginEvents[i];
                }
            }
            this._lastMarginEvent = lastEvent;
            this._lastMarginEventId = this._lastMarginEvent.Id;
            const bannerType: BannerType = BannerType.fromMarginEvent(lastEvent.EventType);
            if (bannerType) {
                Platform.dispatch(SetBanner({
                    banner: {
                        type: bannerType,
                        remoteId: lastEvent.Id,
                        payload: lastEvent
                    }
                }));
            }
        }
    }

    private doProcessCreditPromos = (response: GetAccountStateResponse) => {
        this._lastCreditPromoId = null;
        if (Utils.isArrayNotEmpty(response.CreditPromos) && Utils.isNull(this._lastCreditPromo)) {
            const creditPromo: CreditPromo = response.CreditPromos[0];
            this._lastCreditPromo = creditPromo;
            this._lastCreditPromoId = this._lastCreditPromo.Id;
            this._logger.debug("Display credit promo: " + this._lastCreditPromoId);
            Platform.dispatch(
                SetTradingModal({
                    tradingModalType: TradingModalType.CreditPromo,
                    info: {
                        visible: true,
                        params: {
                            creditPromo
                        }
                    },
                })
            );
            TradeUtil.playPopupSound();
        }
    }

    public doAcceptCreditPromo = async ({promo, status}: DoAcceptCreditPromoPayload) => {
        const request: CreditPromoAcceptRequest = {id: promo.Id};
        const path: string = status === CreditPromoStatus.Accepted ? "Accept" : "Reject";
        Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
        const answer: [HttpReject, CreditPromoAcceptResponse] = await Utils.to(this.sendToCreditPromo(request, path));
        Platform.dispatch(HideLoader({}));
        if (answer[0]) {
            XhrUtil.notifyReject(answer[0]);
        } else {
            Platform.dispatch(
                SetTradingModal({
                    tradingModalType: TradingModalType.CreditPromo,
                    info: {
                        visible: false
                    },
                })
            );
            const {Status} = answer[1];
            Platform.dispatch(ShowPopup({
                popup: {
                    remoteId: promo.Id,
                    type: PopupType.INFO,
                    icon: PopupIconType.INFO,
                    showClose: true,
                    message: {
                        trKey: Status === CreditPromoStatus.Accepted ? TranslationKey.promotionCreditReceived : TranslationKey.promotionCreditRejected,
                        params: [Parameter.Of(TranslationParam.amount, `${promo.Amount}${Currency.sign(promo.AssetName)}`)]
                    },
                    actions: [{type: PopupActionType.OK}]
                }
            }));
        }
    }

    private doProcessPopupNotifications = (response: GetAccountStateResponse) => {
        this._lastPopupNotificationId = null;
        if (Utils.isArrayNotEmpty(response.PopupNotifications) && Utils.isNull(this._lastPopupNotification)) {
            const popupNotification: PopupNotification = response.PopupNotifications[0];
            this._lastPopupNotification = popupNotification;
            this._lastPopupNotificationId = this._lastPopupNotification.Id;
            const isDpk: boolean = popupNotification.PopupActionType === PopupNotificationActionType.ExecuteDeepLink;
            const isOpenPositionAction: boolean = popupNotification.PopupActionType === PopupNotificationActionType.OpenPosition;
            const actions: PopupAction[] = [];
            if (isOpenPositionAction) {
                const {symbols} = Platform.reduxState<StoreState>().symbols;
                const symbol: TradeSymbol = symbols?.get(popupNotification.SymbolId);
                actions.push(
                    {
                        type: PopupActionType.OK,
                        text: {
                            customValue: `${Translations.text(TranslationKey.sell)} ${symbol?.text}`
                        },
                        action: () => {
                            Platform.dispatch(GoingOpenDeal({
                                SymbolId: popupNotification.SymbolId,
                                Direction: PositionDirection.Sell,
                            }));
                        }
                    },
                    {
                        type: PopupActionType.OK,
                        text: {
                            customValue: `${Translations.text(TranslationKey.buy)} ${symbol?.text}`
                        },
                        action: () => {
                            Platform.dispatch(GoingOpenDeal({
                                SymbolId: popupNotification.SymbolId,
                                Direction: PositionDirection.Buy,
                            }));
                        }
                    }
                );
            } else {
                actions.push(
                    {
                        type: PopupActionType.OK, action: () => {
                            if (popupNotification.PopupActionType) {
                                switch (popupNotification.PopupActionType) {
                                    case PopupNotificationActionType.ExecuteDeepLink:
                                        const index: number = popupNotification.Hyperlink?.indexOf("dpk=");
                                        const dpk: string = index > -1 ? popupNotification.Hyperlink?.substring(index + 4) : popupNotification.Hyperlink;
                                        Platform.dispatch(ExecuteDpk({dpk, params: [Parameter.Of("dpk", dpk)]}));
                                        break;
                                    case PopupNotificationActionType.OpenDeposit:
                                        Platform.dispatch(SetTradingModal({
                                            tradingModalType: TradingModalType.Deposit,
                                            info: {
                                                visible: true
                                            }
                                        }));
                                        break;
                                }
                            }
                        }
                    }
                );
            }
            Platform.dispatch(ShowPopup({
                popup: {
                    remoteId: popupNotification.Id,
                    type: PopupType.INFO,
                    message: {
                        customValue: popupNotification.Message
                    },
                    hyperlink: isDpk ? null : popupNotification.Hyperlink,
                    showClose: true,
                    actions
                }
            }));
            TradeUtil.playPopupSound();
        }
    }

    private doProcessAccountOptions = (response: GetAccountStateResponse) => {
        const {ShownMenuItems} = response;
        const {app, withdrawal} = Platform.reduxState<StoreState>();
        let bannerType: BannerType;
        if (withdrawal.withdrawal && app.blockedBanners.indexOf(BannerType.WithdrawalCancel) < 0) {
            bannerType = BannerType.WithdrawalCancel;
        } else if ((ShownMenuItems & AccountOption.FinalizeRegistrationLink) > 0 && app.blockedBanners.indexOf(BannerType.FinalizeRegistration) < 0) {
            bannerType = BannerType.FinalizeRegistration;
        } else if ((ShownMenuItems & AccountOption.ProfessionalAccount) > 0 && app.blockedBanners.indexOf(BannerType.ApplyForProfessional) < 0) {
            bannerType = BannerType.ApplyForProfessional;
        }
        if (Utils.isNotNull(bannerType) && (Utils.isNull(app.banner) || (app.banner.type !== bannerType && Utils.isNull(app.banner.remoteId)))) {
            Platform.dispatch(SetBanner({
                banner: {
                    type: bannerType,
                    payload: withdrawal.withdrawal
                }
            }));
        }
    }

    private doProcessPromotions = (AccountRebateData: RebatePlanData, Vault: VaultInfo, PositionPromotionSettings: PositionPromotion[], response: GetAccountStateResponse) => {
        if (!this._isFirstAccountState) {
            if (
                (Utils.compareUndefined(AccountRebateData, response.AccountRebateData) !== 0 || PromotionUtil.isRebatePlanChanged(AccountRebateData, response.AccountRebateData))
                || (Utils.compareUndefined(Vault, response.Vault) !== 0 || PromotionUtil.isVaultChanged(Vault, response.Vault))
                || (Utils.compareUndefined(PositionPromotionSettings, response.PositionPromotionSettings) !== 0 || PromotionUtil.isPromotionsChanged(PositionPromotionSettings, response.PositionPromotionSettings))
            ) {
                Platform.dispatch(SetPromotionsChanged({changed: true}));
                Platform.dispatch(AddInboxMessage({
                    message: {
                        type: InboxMessageType.PromotionsChanged,
                        parameters: []
                    }
                }));
            }
        } else {
            PromotionUtil.setNextPromotionOverview(response.AccountRebateData, response.Vault, response.PositionPromotionSettings);
            if (Utils.isNull(this._promotionsInterval)) {
                this._promotionsInterval = setInterval(() => {
                    const account: AccountReduxState = Platform.reduxState<StoreState>().account;
                    PromotionUtil.setNextPromotionOverview(account.AccountRebateData, account.Vault, account.PositionPromotionSettings);
                }, 5000);
            }
        }
    }

    public onHidePopup = ({popup}: HidePopupPayload) => {
        if (popup && this._lastCreditPromo && popup.remoteId === this._lastCreditPromo.Id) {
            this._lastCreditPromo = null;
        }
        if (popup && this._lastPopupNotification && popup.remoteId === this._lastPopupNotification.Id) {
            this._lastPopupNotification = null;
        }
    }

    public onRemoveBanner = ({banner}: BannerPayload) => {
        if (banner && this._lastMarginEvent && banner.remoteId === this._lastMarginEvent.Id) {
            this._lastMarginEvent = null;
        }
    }

    public onChangeRoute = (payload): void => {
        const {authenticated} = Platform.reduxState<StoreState>().auth;
        const routeType: RouteType = payload.route.name;
        if (authenticated && routeType === RouteType.AccountSettings) {
            this.doFetchPendingWithdrawal().catch(() => {
            });
        }
    }

    public doProcessTopUp = (payload: OnProcessTopUpButtonPayload): void => {
        const accountState: AccountState = Platform.state(ServiceType.Account);
        const btnType: DepositButtonActionType = accountState.getDepositButtonType();
        if (Utils.isNotNull(btnType)) {
            switch (btnType) {
                case DepositButtonActionType.Kyc:
                    if (payload && payload.navigation) {
                        payload.navigation.navigate(RouteType.Kyc, {page: RouteType.Home});
                    } else {
                        Platform.dispatch(SetTradingModal({
                            tradingModalType: TradingModalType.KYC,
                            info: {
                                visible: true
                            }
                        }));
                    }
                    break;
                case DepositButtonActionType.Deposit:
                    if (payload && payload.navigation) {
                        payload.navigation.navigate(RouteType.Deposit, {page: RouteType.Home});
                    } else {
                        Platform.dispatch(SetTradingModal({
                            tradingModalType: TradingModalType.Deposit,
                            info: {
                                visible: true,
                                params: payload?.parameters
                            }
                        }));
                    }
                    break;
                case DepositButtonActionType.Inappropriate:
                    Platform.dispatch(ShowPopup({
                        popup: {
                            type: PopupType.ERROR,
                            message: {
                                trKey: TranslationKey.notAllowedToTradeError
                            },
                            showClose: true,
                            icon: PopupIconType.ERROR,
                            actions: [{type: PopupActionType.OK}]
                        }
                    }));
                    break;
                default:
            }
        }
        return null;
    }

    public doFetchPendingWithdrawal = async () => {
        const accountState: AccountState = Platform.state(ServiceType.Account);
        if (accountState.accountType === NXEnvironmentType.Live) {
            this._logger.debug("Fetching pending withdrawal");
            const request: GetPendingWithdrawalRequest = {};
            const answer: [HttpReject, GetPendingWithdrawalResponse] = await Utils.to(this.sendToBillingService(request, "GetPendingWithdrawalRequest"));
            if (answer[0]) {
                this._logger.debug("Failed fetch pending withdrawal");
            } else {
                const response: GetPendingWithdrawalResponse = answer[1];
                if (response && response.RequestId) {
                    const {withdrawal} = Platform.reduxState<StoreState>().withdrawal;
                    if (Utils.isNull(withdrawal) || response.Amount !== withdrawal.Amount) {
                        Platform.dispatch(AddInboxMessage({
                            message: {
                                type: InboxMessageType.Withdrawal,
                                parameters: [
                                    Parameter.Of(InboxParameter.TrKey, TranslationKey.bannersWithdrawalProcessingWithdrawal),
                                    Parameter.Of(InboxParameter.TrParams, [
                                        Parameter.Of(TranslationParam.value, `${Currency.sign(response?.PaymentAssetName)}${response.Amount}`)
                                    ])
                                ]
                            }
                        }));
                    }
                    Platform.dispatch(SetPendingWithdrawal({
                        withdrawal: response
                    }));
                } else {
                    this._logger.debug("Doesn't have pending withdrawal");
                }
            }
        }
    }

    public doWithdrawal = async (payload: DoWithdrawalPayload) => {
        this._logger.debug("Try to withdrawal: " + payload.amount);
        const authState: AuthState = Platform.state(ServiceType.Auth);
        const accountState: AccountState = Platform.state(ServiceType.Account);
        const account: LoginAccountInfo = authState.getAccount(accountState.accountType);
        const request: CreateWithdrawalRequest = {
            Amount: payload.amount,
            AssetId: account.BaseAssetId,
            AccountId: accountState.accountId,
            Reason: payload.reason
        };
        Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
        const answer: [HttpReject, any] = await Utils.to(this.sendToBillingService(request, "CreateWithdrawalRequest"));
        Platform.dispatch(HideLoader({}));
        Platform.dispatch(SetTradingModal({
            tradingModalType: TradingModalType.Withdrawal,
            info: {
                visible: false
            }
        }));
        if (answer[0]) {
            XhrUtil.notifyReject(answer[0]);
        } else {
            await this.doFetchPendingWithdrawal();
            Platform.dispatch(SetTradingModal({
                tradingModalType: TradingModalType.WithdrawalSuccess,
                info: {
                    visible: true
                }
            }));
        }
    }

    public doCancelWithdrawal = async (payload: DoCancelWithdrawalPayload) => {
        this._logger.debug("Try cancel withdrawal");
        const withdrawal: WithdrawalPending = payload.withdrawal;
        const request: CancelWithdrawalRequest = {RequestId: withdrawal.RequestId};
        Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
        const answer: [HttpReject, any] = await Utils.to(this.sendToBillingService(request, "CancelWithdrawalRequest"));
        Platform.dispatch(HideLoader({}));
        if (answer[0]) {
            XhrUtil.notifyReject(answer[0]);
        } else {
            Platform.dispatch(SetPendingWithdrawal({
                withdrawal: null
            }));
            Platform.dispatch(AddInboxMessage({
                message: {
                    type: InboxMessageType.Withdrawal,
                    parameters: [
                        Parameter.Of(InboxParameter.TrKey, TranslationKey.withdrawalTitleCanceled),
                        Parameter.Of(InboxParameter.TrParams, [
                            Parameter.Of(TranslationParam.value, withdrawal.RequestId)
                        ])
                    ]
                }
            }));
        }
    }

    private sendToAccountService = (request: any, path: string): Promise<any> => {
        return XhrUtil.sendAuthenticated(request, "WebProfitServer/AccountStatesService.svc/json/" + path);
    }

    private sendToBillingService = (request: any, path: string): Promise<any> => {
        return XhrUtil.sendAuthenticatedTo(ServerType.Billing, request, "BillingServer/MobileBillingService2.svc/json/" + path);
    }

    private sendToCreditPromo = (request: any, path: string): Promise<any> => {
        return XhrUtil.sendAuthenticated(request, "WebProfitSite/CreditPromo/" + path);
    }
}
